/* ============================================================
   FA Carousel System — fa-carousel.js

   Handles: dot population, arrow nav, dot sync on scroll
   Safe for: AJAX re-renders (MutationObserver-aware)
   Shopify editor: disabled automatically in design mode

   Expected markup per carousel:
   ─────────────────────────────────────────────────────────────
   <div class="fa-carousel-mobile">        ← or fa-carousel
     <div class="fa-carousel__track">      ← scroll container
       <!-- cards -->
     </div>
     <div class="fa-carousel__nav">
       <div class="fa-carousel__dots"></div>
       <div class="fa-carousel__arrows">
         <button class="fa-carousel__arrow fa-carousel__arrow--prev">…</button>
         <button class="fa-carousel__arrow fa-carousel__arrow--next">…</button>
       </div>
     </div>
   </div>
   ─────────────────────────────────────────────────────────────
============================================================ */

(function () {
  'use strict';

  /* ── Constants ─────────────────────────────────────────────── */

  const INIT_ATTR   = 'data-fa-carousel-init';
  const CONTAINER   = '.fa-carousel-mobile, .fa-carousel';
  const TRACK       = '.fa-carousel__track';
  const DOTS_WRAP   = '.fa-carousel__dots';
  const PREV_BTN    = '.fa-carousel__arrow--prev';
  const NEXT_BTN    = '.fa-carousel__arrow--next';
  const DOT_CLASS   = 'fa-carousel__dot';
  const DOT_ACTIVE  = 'is-active';

  /* ── Single carousel init ───────────────────────────────────── */

  function initCarousel(container) {
    // Skip if already initialised
    if (container.hasAttribute(INIT_ATTR)) return;

    // Skip in Shopify theme editor — CSS already collapses carousel there
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    const track = container.querySelector(TRACK);
    if (!track) return;

    const cards = Array.from(track.children);
    if (cards.length === 0) return;

    // Mark as initialised before wiring to prevent double-init
    container.setAttribute(INIT_ATTR, '1');

    const count        = cards.length;
    const dotsWrap     = container.querySelector(DOTS_WRAP);
    const prevBtn      = container.querySelector(PREV_BTN);
    const nextBtn      = container.querySelector(NEXT_BTN);

    /* ── Build dots ──────────────────────────────────────────── */

    if (dotsWrap) {
      dotsWrap.innerHTML = cards.map((_, i) =>
        `<button class="${DOT_CLASS}${i === 0 ? ' ' + DOT_ACTIVE : ''}" aria-label="Go to slide ${i + 1}"></button>`
      ).join('');
    }

    const dots = dotsWrap ? Array.from(dotsWrap.querySelectorAll('.' + DOT_CLASS)) : [];

    /* ── Helpers ─────────────────────────────────────────────── */

    // Card step width = card width + gap
    function stepWidth() {
      if (!cards[0]) return 0;
      const gap = parseFloat(getComputedStyle(track).gap) || 12;
      return cards[0].offsetWidth + gap;
    }

    // Which card is currently most visible
    function currentIndex() {
      const step = stepWidth();
      if (step === 0) return 0;
      return Math.min(count - 1, Math.round(track.scrollLeft / step));
    }

    // Update dot highlights + arrow disabled states
    function syncNav(index) {
      dots.forEach((d, i) => d.classList.toggle(DOT_ACTIVE, i === index));
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= count - 1;
    }

    // Scroll track to a card index
    function goTo(index) {
      const clamped = Math.max(0, Math.min(count - 1, index));
      const step    = stepWidth();
      track.scrollTo({ left: clamped * step, behavior: 'smooth' });
      syncNav(clamped);
    }

    /* ── Scroll → dot sync (debounced) ──────────────────────── */

    let scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        syncNav(currentIndex());
      }, 80);
    }, { passive: true });

    /* ── Dot clicks ──────────────────────────────────────────── */

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); });
    });

    /* ── Arrow clicks ────────────────────────────────────────── */

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(currentIndex() - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(currentIndex() + 1);
      });
    }


    /* ── Touch swipe → sync nav after swipe settles ──────────── */
    // CSS scroll-snap handles the snapping; we just need to
    // sync the nav UI once the finger lifts

    track.addEventListener('touchend', function () {
      // Give scroll-snap a moment to settle before reading position
      setTimeout(function () { syncNav(currentIndex()); }, 150);
    }, { passive: true });

    /* ── Set initial state ───────────────────────────────────── */

    syncNav(0);
  }

  /* ── Init all carousels found in a root element ────────────── */

  function initAll(root) {
    root = root || document;
    root.querySelectorAll(CONTAINER).forEach(initCarousel);
  }

  /* ── MutationObserver — re-init after AJAX re-renders ───────── */
  // Watches DOM areas that Shopify / Horizon can AJAX-replace.
  // Only re-inits carousels inside newly-added nodes, so stable
  // carousels elsewhere on the page are never double-initialised.

  function watchForChanges() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'childList') return;

        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // element nodes only

          // If the added node itself is a carousel, init it
          if (node.matches && node.matches(CONTAINER)) {
            node.removeAttribute(INIT_ATTR);
            initCarousel(node);
          }

          // Init any carousels nested inside the added node
          if (node.querySelectorAll) {
            node.querySelectorAll(CONTAINER).forEach(function (el) {
              el.removeAttribute(INIT_ATTR);
              initCarousel(el);
            });
          }
        });
      });
    });

    // Observe areas known to AJAX re-render in Horizon
    var watchTargets = [
      // Cart items area (Horizon AJAX cart)
      document.querySelector('cart-items'),
      document.querySelector('.cart__items'),
      // Cart notification drawer
      document.querySelector('#cart-notification'),
      document.querySelector('cart-notification'),
      // PDP below-fold (recs loaded via Section Rendering API)
      document.querySelector('.fa-pdp__below'),
      document.querySelector('.fa-pdp-recs'),
      // Main content fallback
      document.querySelector('main'),
    ].filter(Boolean);

    // Deduplicate (e.g. if 'main' contains others)
    var seen = new Set();
    watchTargets.forEach(function (target) {
      if (!seen.has(target)) {
        seen.add(target);
        observer.observe(target, { childList: true, subtree: true });
      }
    });
  }

  /* ── Public API — callable by other scripts ─────────────────── */
  // fa-product-shelf.liquid calls this after its dynamic fetch completes
  window.faInitCarousels = initAll;

  /* ── Boot ───────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      watchForChanges();
    });
  } else {
    // DOM already ready (script loaded async/defer)
    initAll();
    watchForChanges();
  }

})();